import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import { Mic } from "lucide-react";
import {
  PanelsTopLeft,
  Projector,
  CheckSquare,
  Clock,
  Calendar,
  BookOpen,
  Newspaper,
  Users,
  GraduationCap,
  BarChart3,
  Settings,
  Search,
  Zap,
  Home,
  UsersRound,
  ArrowRight,
  History,
} from "lucide-react";

const RECENT_PAGES_KEY = "commandbar_recent_pages";
const MAX_RECENT = 5;

interface NavItem {
  label: string;
  path: string;
  icon: JSX.Element;
  keywords?: string;
  adminOnly?: boolean;
  managerOnly?: boolean;
}

const navigationItems: NavItem[] = [
  { label: "Dashboard", path: "/", icon: <PanelsTopLeft className="h-4 w-4" />, keywords: "home main" },
  { label: "Projects", path: "/projects", icon: <Projector className="h-4 w-4" />, keywords: "project list" },
  { label: "My Tasks", path: "/my-tasks", icon: <CheckSquare className="h-4 w-4" />, keywords: "task todo assigned" },
  { label: "Timesheet", path: "/timesheet", icon: <Clock className="h-4 w-4" />, keywords: "time hours log effort" },
  { label: "My Leave", path: "/leave", icon: <Calendar className="h-4 w-4" />, keywords: "leave vacation pto holiday" },
  { label: "Work Location", path: "/work-location", icon: <Home className="h-4 w-4" />, keywords: "wfh remote office" },
  { label: "Knowledge Base", path: "/knowledge", icon: <BookOpen className="h-4 w-4" />, keywords: "wiki docs articles" },
  { label: "News", path: "/news", icon: <Newspaper className="h-4 w-4" />, keywords: "news updates announcements" },
  { label: "People Directory", path: "/directory", icon: <Users className="h-4 w-4" />, keywords: "directory people contacts employees" },
  { label: "Training", path: "/training", icon: <GraduationCap className="h-4 w-4" />, keywords: "training courses learn" },
  { label: "Resources", path: "/resources", icon: <UsersRound className="h-4 w-4" />, keywords: "resources allocation capacity", managerOnly: true },
  { label: "Analytics Hub", path: "/analytics", icon: <BarChart3 className="h-4 w-4" />, keywords: "analytics reports charts", managerOnly: true },
  { label: "Leave Approvals", path: "/leave-approvals", icon: <Calendar className="h-4 w-4" />, keywords: "approve reject leave", managerOnly: true },
  { label: "Manager Command Center", path: "/manager-dashboard", icon: <Zap className="h-4 w-4" />, keywords: "command center manager kpi", managerOnly: true },
  { label: "Admin & Tools", path: "/admin", icon: <Settings className="h-4 w-4" />, keywords: "admin settings tools", adminOnly: true },
];

const quickActions: NavItem[] = [
  { label: "Log Effort", path: "action:quicklog", icon: <Clock className="h-4 w-4" />, keywords: "log effort hours time" },
  { label: "Apply Leave", path: "/leave", icon: <Calendar className="h-4 w-4" />, keywords: "apply leave vacation request" },
  { label: "Search People", path: "/directory", icon: <Search className="h-4 w-4" />, keywords: "search find people employee" },
  { label: "Voice Input", path: "action:voiceinput", icon: <Mic className="h-4 w-4" />, keywords: "voice mic microphone dictate speak speech" },
];

function getRecentPages(): { label: string; path: string }[] {
  try {
    const stored = localStorage.getItem(RECENT_PAGES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function addRecentPage(label: string, path: string) {
  try {
    const recent = getRecentPages().filter(r => r.path !== path);
    recent.unshift({ label, path });
    localStorage.setItem(RECENT_PAGES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch {
    // Ignore localStorage errors
  }
}

interface CommandBarProps {
  onQuickLog?: () => void;
}

export default function CommandBar({ onQuickLog }: CommandBarProps) {
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();
  const { isAdmin, isTeamHead } = useAuth();
  const isManager = isAdmin || isTeamHead;

  // Global keyboard listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const filteredNav = navigationItems.filter(item => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.managerOnly && !isManager) return false;
    return true;
  });

  const recentPages = getRecentPages();

  const handleSelect = useCallback((path: string) => {
    setOpen(false);
    if (path === "action:quicklog") {
      onQuickLog?.();
      return;
    }
    if (path === "action:voiceinput") {
      // Trigger the global voice input shortcut
      window.dispatchEvent(
        new CustomEvent("voice-input-shortcut", { detail: { fieldId: null } })
      );
      return;
    }
    navigate(path);
  }, [navigate, onQuickLog]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList className="max-h-[60vh]">
        <CommandEmpty>No results found.</CommandEmpty>

        {recentPages.length > 0 && (
          <CommandGroup heading="Recent">
            {recentPages.map((page) => (
              <CommandItem key={page.path} value={`recent-${page.path}`} onSelect={() => handleSelect(page.path)}>
                <History className="h-4 w-4 mr-2 text-muted-foreground" />
                <span>{page.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandSeparator />

        <CommandGroup heading="Quick Actions">
          {quickActions.map((action) => (
            <CommandItem key={action.path} value={`action-${action.label} ${action.keywords || ""}`} onSelect={() => handleSelect(action.path)}>
              {action.icon}
              <span className="ml-2">{action.label}</span>
              <ArrowRight className="ml-auto h-3 w-3 text-muted-foreground" />
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Navigation">
          {filteredNav.map((item) => (
            <CommandItem key={item.path} value={`nav-${item.label} ${item.keywords || ""}`} onSelect={() => handleSelect(item.path)}>
              {item.icon}
              <span className="ml-2">{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
      <div className="border-t px-3 py-2 flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <span><kbd className="px-1 py-0.5 rounded bg-muted text-muted-foreground font-mono">↑↓</kbd> navigate</span>
          <span><kbd className="px-1 py-0.5 rounded bg-muted text-muted-foreground font-mono">↵</kbd> select</span>
          <span><kbd className="px-1 py-0.5 rounded bg-muted text-muted-foreground font-mono">esc</kbd> close</span>
        </div>
      </div>
    </CommandDialog>
  );
}
